/**
 * Rozdělení letáků do záložek podle stavu. Sdílené mezi seznamem letáků
 * dodavatele (FlyersListPage) a přehledem pro admina a schvalovatelskou dvojici
 * (ActiveFlyersPage), aby se pravidla nerozešla.
 *
 * Pokrytí stavů (enum FlyerStatus má sedm hodnot):
 *   active                     → Aktivní letáky
 *   draft bez rejectionReason  → Koncepty
 *   pending_approval           → Čeká na schválení
 *     + pending_verification a approved se dnes nepoužívají, ale kdyby se
 *       workflow změnilo, spadnou sem a nikde se neztratí
 *   draft s rejectionReason    → Zamítnuté
 *     + enum rejected: zamítnutí se dnes ukládá jako draft + důvod, hodnota
 *       enumu se nepoužívá; kdyby ji něco nastavilo, patří taky sem
 *   expired                    → Archiv
 * Smazané se nenabízejí - mazání je trvalé; měkké mazání zamítnuto v 09/2026
 * (docs/processed/navrh-smazane-letaky.md).
 */
export type Zalozka = 'active' | 'draft' | 'ceka' | 'rejected' | 'archive' | 'deleted';

export const ZALOZKA_POPISKY: Record<Zalozka, string> = {
  active: 'Aktivní letáky',
  draft: 'Koncepty',
  ceka: 'Čeká na schválení',
  rejected: 'Zamítnuté',
  archive: 'Archiv',
  deleted: 'Smazané',
};

export type LetakProZalozku = { status: string; rejectionReason?: string | null };

export const sedneNaZalozku = (
  flyer: LetakProZalozku,
  zalozka: Zalozka,
  /** U prodejce je „Aktuální" všechno, co není v archivu - jiné stavy nemá. */
  jeProdejce = false,
): boolean => {
  switch (zalozka) {
    case 'active':
      return flyer.status === 'active';
    case 'draft':
      return jeProdejce
        ? flyer.status !== 'expired'
        : flyer.status === 'draft' && !flyer.rejectionReason;
    case 'rejected':
      return (flyer.status === 'draft' && !!flyer.rejectionReason) || flyer.status === 'rejected';
    case 'ceka':
      return ['pending_approval', 'pending_verification', 'approved'].includes(flyer.status);
    case 'archive':
      return flyer.status === 'expired';
    case 'deleted':
      return false;
    default:
      return false;
  }
};

/**
 * Archivovaný leták prodejce s uplynulou platností je už jen záznam v seznamu:
 * nedá se obnovit ani zobrazit či stáhnout jeho PDF. Rozhodnuto 23. 9. 2026.
 *
 * Archivace vždy ukončí platnost ke včerejšku, takže to v praxi platí pro každý
 * archivovaný leták prodejce - archivace je tím nevratná. Podmínka na datum je
 * tu proto, aby se pravidlo nerozešlo, kdyby archivace datum přestala přepisovat.
 *
 * Totéž hlídá i backend (findOneForPdf, unarchiveFlyer); tady je to jen proto,
 * aby se uživateli nenabízelo tlačítko, které by stejně skončilo chybou.
 */
export const jeUzavrenyArchivProdejce = (
  flyer: { status: string; validTo?: string | null },
  jeProdejce: boolean,
): boolean => {
  if (!jeProdejce || flyer.status !== 'expired') return false;
  if (!flyer.validTo) return true;

  const ted = new Date();
  const dnesUtc = Date.UTC(ted.getUTCFullYear(), ted.getUTCMonth(), ted.getUTCDate());
  return new Date(flyer.validTo).getTime() < dnesUtc;
};

export type LetakProRazeni = {
  validTo?: string | null;
  validFrom?: string | null;
  createdAt?: string | null;
  id: string;
};

/**
 * Řazení uvnitř záložky: platnost do, pak platnost od, pak datum vytvoření.
 * Sestupně, aby nahoře byl nejnovější leták.
 *
 * Id je až poslední a je tu jen pro stabilitu pořadí - je to UUID, takže nenese
 * žádnou informaci o čase a samo o sobě by řadilo náhodně. Rozhoduje jen mezi
 * letáky, které se shodují ve všech třech datech.
 */
export const porovnejLetaky = (a: LetakProRazeni, b: LetakProRazeni): number => {
  const cas = (d?: string | null) => (d ? new Date(d).getTime() : 0);
  return (
    cas(b.validTo) - cas(a.validTo) ||
    cas(b.validFrom) - cas(a.validFrom) ||
    cas(b.createdAt) - cas(a.createdAt) ||
    b.id.localeCompare(a.id)
  );
};
